import {
  type SelectionAiActionId,
  type SelectionInsightActionId,
} from '@/lib/selection-ai-prompts';
import { type Editor } from '@tiptap/react';
import { atom } from 'jotai';

/**
 * One-shot request to run an inline AI edit (selection → Ask Heidi).
 * Set by `SelectionAiActions` when an edit action is clicked; consumed (and
 * cleared) by the `useInlineSelectionEdit` controller mounted next to the
 * matching editor. Carries the editor instance so that only the editor the
 * selection was made in picks the request up (several block editors can be
 * mounted at once). In-memory only — never persisted.
 */

/**
 * What the request produces:
 * - 'edit': a replacement for the highlighted text (Accept / Insert below)
 * - 'insight': a key-points summary about the highlighted text, with a
 *   "View in Transcript" hand-off that highlights excerpts in the Transcript tab
 */
export type InlineEditIntent = 'edit' | 'insight';

/** Preset action ids plus the free-text "ask anything" prompt (lite input). */
export type InlineEditActionId = SelectionAiActionId | 'ask_anything';

type InlineSelectionEditRequestBase = {
  editor: Editor;
  from: number;
  to: number;
  text: string;
};

/** Preset rewrite/edit action — runs immediately with a composed prompt. */
export type InlineSelectionEditPresetRequest = InlineSelectionEditRequestBase & {
  intent: 'edit';
  actionId: SelectionAiActionId;
};

/** Insight action — key-points inline panel with Read-more hand-off. */
export type InlineSelectionEditInsightRequest = InlineSelectionEditRequestBase & {
  intent: 'insight';
  actionId: SelectionInsightActionId;
};

/** Ask anything — open the lite prompt input instead of running immediately. */
export type InlineSelectionEditPromptInputRequest = InlineSelectionEditRequestBase & {
  intent: 'edit';
  actionId: 'ask_anything';
  promptInput: true;
};

/** Ask anything (or reprompt) — run with a caller-supplied prompt. */
export type InlineSelectionEditCustomPromptRequest = InlineSelectionEditRequestBase & {
  intent: 'edit';
  actionId: 'ask_anything';
  customPrompt: string;
};

export type InlineSelectionEditRequest =
  | InlineSelectionEditPresetRequest
  | InlineSelectionEditInsightRequest
  | InlineSelectionEditPromptInputRequest
  | InlineSelectionEditCustomPromptRequest;

export const inlineSelectionEditRequestAtom = atom<InlineSelectionEditRequest | null>(
  null
);

/**
 * Ref-count of inline-edit controllers currently in a non-idle phase. Several
 * block editors mount at once; a single global boolean would be cleared by any
 * idle instance and break the floating-bar hand-off.
 */
export const inlineSelectionEditActiveCountAtom = atom(0);

/**
 * True while any inline edit flow (lite prompt / generating / review) is
 * showing on any editor. The bottom floating Ask Heidi bar reads this to slide
 * itself out of the way and back in when the inline panel closes.
 */
export const inlineSelectionEditActiveAtom = atom(
  (get) => get(inlineSelectionEditActiveCountAtom) > 0
);
